import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FileItem, FileUploader, FileUploaderOptions, FileUploadModule } from 'ng2-file-upload';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { MixpanelService } from '@app/services/mixpanel.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DocumentsService } from '@app/services/documents.service';
import { SnackbarService } from '@app/services/snackbar.service';
import { FileUploaderService, isFileUploadResponse } from '@app/services/file-uploader.service';
import {
    FileInterface,
    getSimpleFileTypeFromName,
    NewlyUploadedFile,
    SupportedFileExtensions,
} from '@app/interfaces/file.interface';
import { DocumentGroupEnum } from '@app/enums/document-group.enum';
import QRCode from 'qrcode';
import { UsersService } from '@app/services/users.service';
import { environment } from '@env/environment';
import { MagicLinksService } from '@app/services/magic-links.service';
import { MagicLinkInterface } from '@app/interfaces/magic-link.interface';
import { isSavedSnapshotInterface, Snapshot, SnapshotsService } from '@app/services/snapshots.service';
import { bounceInOnEnterAnimation, zoomInOnEnterAnimation, zoomOutOnLeaveAnimation } from 'angular-animations';
import { DocumentsUtils } from '@app/modules/3-documents/documents.utils';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FilePreviewComponent } from '@app/components/file-preview/file-preview.component';
import { MatRippleModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { DialogService } from '@app/modules/dialogs/dialog.service';
import { DeviceService } from '@app/services/device.service';
import { InfoPanelComponent } from '@app/components/info-panel/info-panel.component';
import { HasErrorDirective } from '@app/directives/validation/has-error.directive';
import { FormService } from '@app/services/form.service';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { Platform } from '@angular/cdk/platform';
import { FilesService } from '@app/services/files.service';
import { PdfViewerComponent } from '@app/components/pdf-viewer/pdf-viewer.component';
import { MainService } from '@app/services/main.service';
import {
    FileAction,
    FileItemComponent,
    FilePlaceholder,
} from '@app/modules/dialogs/upload-dialog/components/file-item/file-item.component';
import { CameraSnapshotComponent } from '@app/modules/dialogs/upload-dialog/components/camera-snapshot/camera-snapshot.component';
import { SnapshotFileItem } from '@app/modules/dialogs/upload-dialog/snapshot-file-item';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApplicantNameOrIndexByApplicantIdPipe } from '@app/pipes/applicant-name-or-index-by-id.pipe';
import { RichFormControlDirective } from '@app/directives/validation/rich-form-control.directive';
import { DocumentInterface } from '@app/interfaces/document.interface';
import { ProviderNameById } from '@app/pipes/providers.pipe';
import { DocumentStatusEnum } from '@app/enums/document-status.enum';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { MortgageInterface } from '@app/interfaces/mortgage.interface';
import { MortgagesService } from '@app/services/mortgages.service';

class UploadedFileItem extends FileItem {
    constructor(name: string) {
        super({ options: { url: null } } as FileUploader, { name } as File, {} as FileUploaderOptions);
    }

    override upload() {
        this._onSuccess(null, null, null);
    }
}

export interface UploadDialogData {
    document: DocumentInterface;
    documentObservable: Observable<DocumentInterface>;
    documents: DocumentInterface[];
    mortgage: MortgageInterface;
}

export interface FileWrapper {
    action?: FileAction;
    documentFile?: FileInterface;
    file: FileItem;
    showCheckMark?: boolean;
}

@Component({
    selector: 'app-dialog-upload',
    styleUrls: ['./upload-dialog.component.scss'],
    templateUrl: './upload-dialog.component.html',
    animations: [
        bounceInOnEnterAnimation(),
        zoomOutOnLeaveAnimation({
            delay: 0,
            duration: 300,
        }),
        zoomInOnEnterAnimation({ duration: 100 }),
    ],
    imports: [
        FileItemComponent,
        FilePreviewComponent,
        MatRippleModule,
        MatRadioModule,
        FileUploadModule,
        MatDialogModule,
        InfoPanelComponent,
        HasErrorDirective,
        RichFormControlDirective,
        CameraSnapshotComponent,
        PdfViewerComponent,
        MatIconModule,
        ReactiveFormsModule,
        MatExpansionModule,
        NgClass,
        MatProgressSpinnerModule,
        ProviderNameById,
        ApplicantNameOrIndexByApplicantIdPipe,
        AsyncPipe,
        MatButton,
        MatMiniFabButton,
    ],
})
export class UploadDialogComponent implements OnInit, OnDestroy, AfterViewInit {
    dialogRef = inject<MatDialogRef<UploadDialogComponent>>(MatDialogRef);
    data = inject<UploadDialogData>(MAT_DIALOG_DATA);

    private readonly fileUploaderService = inject(FileUploaderService);
    private readonly mixpanelService = inject(MixpanelService);
    private readonly documentsService = inject(DocumentsService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly userService = inject(UsersService);
    private readonly magicLinksService = inject(MagicLinksService);
    private readonly snapshotsService = inject(SnapshotsService);
    private readonly dialogService = inject(DialogService);
    public readonly deviceService = inject(DeviceService);
    public readonly formService = inject(FormService);
    private readonly platform = inject(Platform);
    private readonly filesService = inject(FilesService);
    protected readonly mortgagesService = inject(MortgagesService);
    protected readonly mainService = inject(MainService);

    protected destroyed$: Subject<void> = new Subject<void>();

    public DocumentGroupEnum: typeof DocumentGroupEnum = DocumentGroupEnum;
    public animationInProgress: boolean = false;
    public cameraSupported: boolean = false;
    public document: DocumentInterface;
    public documentTypeControl: FormControl<string>;
    public documentTypes?: string[];
    public documents: DocumentInterface[] = [];
    public fileDropError: boolean = false;
    public filePreviewIndex: number = 0;
    public filesToDownloadForSignature: FileWrapper[] = [];
    public filesToPreview: FileInterface[];
    public hasFileOver: boolean = false;
    public hasSomeRejectedFiles: boolean = false;
    public isDesktop: boolean;
    public isUploadable: boolean = true;
    public magicLinkUrl: string;
    public magicLinkUrlLoaded: boolean = false;
    public mortgage: MortgageInterface;
    public previewIsVisible: boolean;
    public queue: FileWrapper[] = [];
    public rejectionReasons: string[] = [];
    public showCameraPreview: boolean = false;
    public showInfoBox: boolean = false;
    public uploadByMobileExpanded: boolean = false;
    public uploader: FileUploader;
    public waitingForSignature: boolean = false;
    private canvasElement: HTMLCanvasElement;
    private filesQueueElement: HTMLDivElement;
    private qrCodeExpansionPanel: MatExpansionPanel;
    public filesChanged: boolean = false;
    public pdfVisible = false;
    public pdfFileUrl: string;
    public pdfFilename: string;

    @ViewChild('canvas')
    public set qrCodeCanvas(qrCodeCanvas: ElementRef<HTMLCanvasElement>) {
        this.canvasElement = qrCodeCanvas?.nativeElement;
    }

    @ViewChild('filesQueue')
    public set filesQueue(filesQueue: ElementRef<HTMLDivElement>) {
        this.filesQueueElement = filesQueue?.nativeElement;
    }

    @ViewChild('expansionPanel')
    public set expansionPanel(expansionPanel: MatExpansionPanel) {
        this.qrCodeExpansionPanel = expansionPanel;
    }

    async ngAfterViewInit() {
        if (this.canvasElement) {
            const uploadUrl = await this.generateMagicLinkForUplodViaMobile();
            QRCode.toCanvas(this.canvasElement, uploadUrl, {
                errorCorrectionLevel: 'H',
                width: 150,
                color: { dark: '#0d3353' },
            });
            this.magicLinkUrl = uploadUrl;
            this.magicLinkUrlLoaded = true;
        }
    }

    ngOnInit(): void {
        this.document = this.data?.document;
        this.documents = this.data?.documents;
        this.mortgage = this.data?.mortgage;

        this.deviceService.isDesktopObservable.pipe(takeUntil(this.destroyed$)).subscribe((isDesktop: boolean) => {
            this.isDesktop = isDesktop;
        });

        this.fileUploaderService.uploaderObservable
            .pipe(takeUntil(this.destroyed$))
            .subscribe((uploader: FileUploader) => {
                this.uploader = uploader;
                this.uploader.setOptions({
                    url: environment.url.backendApi + '/files/upload',
                    autoUpload: false,
                    removeAfterUpload: false,
                    queueLimit: 99999,
                });
            });

        this.init();

        this.data.documentObservable
            .pipe(takeUntil(this.destroyed$), debounceTime(200))
            .subscribe((document: DocumentInterface) => {
                this.document = document;
                this.init();
            });

        this.fileUploaderService.fileAddedObservable
            .pipe(takeUntil(this.destroyed$))
            .subscribe((fileItem: FileItem) => {
                this.queue.push({
                    file: fileItem,
                    action: 'delete',
                });

                this.filesChanged = true;
            });

        this.snapshotsService.createdSnapshots$.pipe(takeUntil(this.destroyed$)).subscribe((snapshot: Snapshot) => {
            this.queue.push({ file: new SnapshotFileItem(snapshot, this.snapshotsService), action: 'delete' });
            this.qrCodeExpansionPanel.close();
            this.filesQueueElement.scrollIntoView({ behavior: 'smooth' });
            this.filesChanged = true;
        });

        this.dialogRef.backdropClick().subscribe(() => {
            this.closeDialog();
        });

        this.cameraSupported = 'mediaDevices' in navigator;
    }

    ngOnDestroy(): void {
        this.destroyed$?.next();
        this.destroyed$?.complete();
    }

    public saveSnapshotFromWebcam(file: File) {
        this.uploader.addToQueue([file]);
    }

    public showPreview(file: FileInterface): void {
        if (!file) {
            return;
        }

        if (file.type === 'pdf' && (this.platform.ANDROID || this.platform.IOS)) {
            this.pdfFileUrl = this.filesService.getFileGetUrl(file.path);
            this.pdfFilename = file.name;
            this.pdfVisible = true;
            return;
        }

        if (file.type === 'pdf' || file.type === 'image') {
            this.filesToPreview = this.document.files
                .map((currentFile) => {
                    if (!currentFile.type) {
                        currentFile.type = getSimpleFileTypeFromName(file.name);
                    }
                    return currentFile;
                })
                .filter((documentFile: FileInterface) => documentFile.type === 'image' || documentFile.type === 'pdf');

            this.filePreviewIndex = this.filesToPreview.findIndex(
                (documentFile: FileInterface) => documentFile.path === file.path
            );
            if (this.filePreviewIndex !== -1) {
                this.previewIsVisible = true;
            }
        } else {
            if (this.platform.ANDROID) {
                const url = this.filesService.getFileGetUrl(file.path);
                window.open(url, '_blank');
            } else {
                const url = this.filesService.getFileDownloadUrl(file.path, file.name);
                window.open(url, '_blank');
            }
        }
    }

    public async uploadFiles() {
        if (this.document._template?.type?.length > 1 && !this.formService.validOrFindError(this.documentTypeControl)) {
            return;
        }

        if (!this.filesChanged) {
            this.fileDropError = true;
            return;
        }

        this.fileDropError = false;
        this.mainService.showLoader = true;

        try {
            if (this.queue.some((fileWrapper) => fileWrapper.documentFile?.status === 'rejected')) {
                const shallContinue = await firstValueFrom(
                    this.dialogService.confirm({
                        title: 'Upozornění',
                        message: 'Zamítnuté soubory budou smazány. Opravdu chcete pokračovat?',
                        okText: 'Pokračovat',
                    })
                );

                if (!shallContinue) {
                    return;
                }
            }

            const uploadedFiles: FileInterface[] = await Promise.all<FileInterface>(
                this.queue
                    .filter((fileWrapper: FileWrapper) => fileWrapper.documentFile?.status !== 'rejected')
                    .map(
                        (fileWrapper: FileWrapper) =>
                            new Promise<FileInterface>((resolve, reject) => {
                                if (fileWrapper.action === 'locked') {
                                    resolve(fileWrapper.documentFile);
                                    return;
                                }

                                fileWrapper.file.onSuccess = (response: string) => {
                                    if (fileWrapper.file instanceof UploadedFileItem) {
                                        resolve(fileWrapper.documentFile);
                                        return;
                                    }

                                    fileWrapper.action = 'locked';
                                    const responseObj = JSON.parse(response);

                                    let uploadedFile: FileInterface;
                                    if (isSavedSnapshotInterface(responseObj)) {
                                        uploadedFile = new NewlyUploadedFile(
                                            responseObj.snapshot.originalName,
                                            responseObj.snapshot.id,
                                            0
                                        );
                                    } else if (isFileUploadResponse(responseObj)) {
                                        uploadedFile = new NewlyUploadedFile(
                                            responseObj.originalname ?? fileWrapper.file.file.name,
                                            responseObj.id,
                                            responseObj.size
                                        );
                                    } else {
                                        reject(new Error('Invalid response'));
                                    }

                                    this.mixpanelService.track('$document_uploaded', {
                                        fileName: uploadedFile.name,
                                    });

                                    resolve(uploadedFile);
                                };

                                fileWrapper.file.onError = (response: string) => {
                                    reject(response);
                                };

                                // Tento radek zmrsi nazev souboru napr. ze "Snímek obrazovky" udela "Sni%CC%mek%20obrazovky"
                                // fileWrapper.file.file.name = encodeURIComponent(fileWrapper.file.file.name);

                                fileWrapper.file.upload();
                            })
                    )
            );

            const hiddenFiles = this.document.files.filter((file) => file.isHidden);
            uploadedFiles.push(...hiddenFiles);

            const forSignatureFiles = this.document.files.filter((file) => file.isDeliveredByUs && !file.isHidden);
            uploadedFiles.push(...forSignatureFiles);

            await this.updateDocuments(uploadedFiles);
            this.mainService.showLoader = false;
            this.closeDialog();
        } finally {
            this.isUploadable = false;
            setTimeout(() => {
                this.mainService.showLoader = false;
            }, 300);
        }
    }

    public fileIconActionClick(file: FileWrapper, index: number): void {
        if (file.action === 'delete') {
            if (!file.documentFile) {
                file.file.remove();
            }
            this.queue.splice(index, 1);
        }
    }

    public closeDialog(): void {
        if (this.filesChanged) {
            this.dialogService
                .confirm({
                    title: 'Chcete zahodit soubory?',
                    message: 'Pokud okno zavřete, nahrané soubory nebudou uloženy',
                    okText: 'Pokračovat v nahrávání',
                    cancelText: 'Zahodit',
                })
                .subscribe((result) => {
                    if (!result) {
                        this.uploader.cancelAll();
                        this.uploader.clearQueue();
                        this.dialogRef.close();
                    }
                });
        } else {
            this.uploader.cancelAll();
            this.uploader.clearQueue();
            this.dialogRef.close();
        }
    }

    public cameraMode() {
        this.showCameraPreview = true;
        this.uploadByMobileExpanded = false;
    }

    // In case that there is more samples: {{ sample.providerId | providerNameById | async}}
    public createSampleFilePlaceholder(fileName: string): FilePlaceholder {
        return `sample.${fileName.split('.').pop() as SupportedFileExtensions}`;
    }

    private init() {
        if (this.document._template.groupId === DocumentGroupEnum.ForIdentification) {
            if (this.document.status === 'done') {
                if (this.document.type) {
                    this.documentTypes = [this.document.type];
                }
            } else {
                let alreadyUsedTypes = this.documents
                    ?.filter((document) => document.applicantIndex === this.document.applicantIndex)
                    .map((document) => document.type)
                    .filter((documentType) => documentType);

                if (alreadyUsedTypes) {
                    alreadyUsedTypes = alreadyUsedTypes.filter(
                        (alreadyUsedType) => alreadyUsedType !== this.document.type
                    );
                    this.documentTypes = this.document._template.type.filter(
                        (templateType) => !alreadyUsedTypes.includes(templateType)
                    );
                }
            }
        }

        if (!this.document.type && this.documentTypes?.length === 1) {
            this.document.type = this.documentTypes[0];
        }

        this.waitingForSignature = DocumentsUtils.isWaitingForSignature(this.document);
        this.hasSomeRejectedFiles =
            this.document.status === DocumentStatusEnum.denied ||
            this.document.files.some((file) => file.status === 'rejected');
        this.rejectionReasons = this.document.files.map((file) => file.rejectionReason).filter((reason) => reason);
        this.isUploadable =
            this.hasSomeRejectedFiles ||
            [DocumentStatusEnum.waiting, DocumentStatusEnum.checking].includes(this.document.status);
        this.showInfoBox = [DocumentStatusEnum.checking, DocumentStatusEnum.checked, DocumentStatusEnum.done].includes(
            this.document.status
        );

        this.documentTypeControl = new FormControl<string>(
            { value: this.document.type, disabled: !this.isUploadable },
            Validators.required
        );

        if (this.document.files.length > 0) {
            this.filesToDownloadForSignature = this.document.files
                ?.filter((file) => file.isDeliveredByUs && !file.isHidden)
                .map((file) => ({
                    file: new UploadedFileItem(file.name),
                    documentFile: file,
                }));
        }

        this.queue = [];
        this.document.files
            .filter((file) => !file.isHidden && !file.isDeliveredByUs)
            .forEach((file: FileInterface) => {
                if (this.document.status === DocumentStatusEnum.denied && file.status !== 'rejected') {
                    file.status = 'rejected';
                }

                this.queue.push({
                    file: new UploadedFileItem(file.name),
                    action:
                        this.document.status === DocumentStatusEnum.denied || file.status === 'rejected'
                            ? 'delete'
                            : 'locked',
                    documentFile: file,
                    showCheckMark: file.status !== 'rejected' && this.hasSomeRejectedFiles,
                });
            });

        this.mainService.showLoader = false;
    }

    private async updateDocuments(uploadedFiles: FileInterface[]): Promise<void> {
        let status: DocumentStatusEnum = null;
        const mortgagePatch: Partial<MortgageInterface> = {};

        if (uploadedFiles.length) {
            status = DocumentStatusEnum.checking;
            mortgagePatch.documentStatusChanged = true;
        }

        const scorePatch = this.mortgagesService.getScorePatch(this.mortgage);

        try {
            // @API documents.patch
            await this.documentsService.patch(this.document._id, {
                deliveredAt: new Date(),
                files: uploadedFiles,
                status,
                type: this.documentTypeControl.value,
            });

            this.filesChanged = false;
            // @API cases/mortgage.patch
            await this.mortgagesService.patch(this.mortgage._id, { ...mortgagePatch, ...scorePatch });
        } catch (e) {
            console.error(e);
            this.snackbarService.showError(
                'Něco se pokazilo, prosíme, kontaktujte telefonicky nebo e-mailem našeho specialistu.'
            );
        }
    }

    private async generateMagicLinkForUplodViaMobile(): Promise<string> {
        const now = new Date();
        const validUntil = new Date(now);
        validUntil.setDate(validUntil.getDate() + 3);

        const item = {
            token: {
                userId: this.userService.user._id,
                email: this.userService.user.email,
                redirectUri: 'camera-upload',
            },
            link: '',
            validUntil,
        };
        // @API magic-links.create
        const shortenedUrl: MagicLinkInterface = await this.magicLinksService.create(item);

        return shortenedUrl.short;
    }

    qrCodePanelExpanded() {
        this.mixpanelService.track('$qrcode_expanded', {
            mortgageId: this.mortgage._id,
        });
    }
}
